/**
 * User Licenses Domain Seed Data
 *
 * Contains all seed data for User License management:
 * - User Licenses (professional certifications, NMLS individual licenses)
 * - License Types
 * - License Status tracking
 *
 * @see PR #237 - User/Employee Management
 */

import { seedId } from '../utils';
import { BUSINESS_IDS } from './tenants';
import { USER_IDS } from './users';

// =============================================================================
// USER LICENSE IDs
// =============================================================================

export const USER_LICENSE_IDS = {
  // OpsMatrix Capital user licenses
  RACHEL_NMLS: seedId('user-license-rachel-nmls', 1),
  RACHEL_STATE_TX: seedId('user-license-rachel-tx', 2),
  MIKE_NMLS: seedId('user-license-mike-nmls', 3),
  MIKE_STATE_TX: seedId('user-license-mike-tx', 4),
  SARAH_NMLS: seedId('user-license-sarah-nmls', 5),
  SARAH_STATE_FL: seedId('user-license-sarah-fl', 6),
  DAVID_NMLS: seedId('user-license-david-nmls', 7),
  EMILY_NMLS: seedId('user-license-emily-nmls', 8),

  // 1Sharpe Capital user licenses
  SHARPE_ADMIN_NMLS: seedId('user-license-sharpe-admin-nmls', 1),
  SHARPE_ADMIN_STATE_GA: seedId('user-license-sharpe-admin-ga', 2),
} as const;

export type UserLicenseId = (typeof USER_LICENSE_IDS)[keyof typeof USER_LICENSE_IDS];

// =============================================================================
// LICENSE TYPE CONSTANTS
// =============================================================================

export const LICENSE_TYPES = {
  NMLS_INDIVIDUAL: 'NMLS_INDIVIDUAL',
  STATE_MLO: 'STATE_MLO',
  DBA: 'DBA',
  REAL_ESTATE: 'REAL_ESTATE',
  INSURANCE: 'INSURANCE',
  OTHER: 'OTHER',
} as const;

export type LicenseType = (typeof LICENSE_TYPES)[keyof typeof LICENSE_TYPES];

export const USER_LICENSE_STATUSES = {
  ACTIVE: 'ACTIVE',
  PENDING: 'PENDING',
  EXPIRED: 'EXPIRED',
  SUSPENDED: 'SUSPENDED',
  REVOKED: 'REVOKED',
  SURRENDERED: 'SURRENDERED',
} as const;

export type UserLicenseStatus = (typeof USER_LICENSE_STATUSES)[keyof typeof USER_LICENSE_STATUSES];

// =============================================================================
// DATA TYPES
// =============================================================================

export interface UserLicenseSeedData {
  id: string;
  userId: string;
  businessId: string;
  licenseType: LicenseType;
  licenseNumber: string;
  licenseName?: string;
  state?: string;
  status: UserLicenseStatus;
  issuedAt?: string;
  expiresAt?: string;
  renewalDate?: string;
  continuingEducationDue?: string;
  verificationUrl?: string;
  notes?: string;
  metadata?: Record<string, unknown>;
}

// =============================================================================
// USER LICENSE DATA
// =============================================================================

export const USER_LICENSES: Record<keyof typeof USER_LICENSE_IDS, UserLicenseSeedData> = {
  // Rachel Martinez - Branch Manager (multiple states)
  RACHEL_NMLS: {
    id: USER_LICENSE_IDS.RACHEL_NMLS,
    userId: USER_IDS.RACHEL_MARTINEZ,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    licenseType: LICENSE_TYPES.NMLS_INDIVIDUAL,
    licenseNumber: 'MLO-123456',
    licenseName: 'NMLS MLO License',
    status: USER_LICENSE_STATUSES.ACTIVE,
    issuedAt: '2018-06-15',
    renewalDate: '2026-12-31',
    continuingEducationDue: '2026-12-31',
    verificationUrl: 'https://www.nmlsconsumeraccess.org/EntityDetails.aspx/INDIVIDUAL/123456',
    notes: 'Branch manager, all states approved',
  },
  RACHEL_STATE_TX: {
    id: USER_LICENSE_IDS.RACHEL_STATE_TX,
    userId: USER_IDS.RACHEL_MARTINEZ,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    licenseType: LICENSE_TYPES.STATE_MLO,
    licenseNumber: 'TX-MLO-123456',
    licenseName: 'Texas MLO License',
    state: 'TX',
    status: USER_LICENSE_STATUSES.ACTIVE,
    issuedAt: '2018-06-15',
    expiresAt: '2026-12-31',
    verificationUrl: 'https://www.sml.texas.gov/',
  },

  // Mike Chen - Loan Officer
  MIKE_NMLS: {
    id: USER_LICENSE_IDS.MIKE_NMLS,
    userId: USER_IDS.MIKE_CHEN,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    licenseType: LICENSE_TYPES.NMLS_INDIVIDUAL,
    licenseNumber: 'MLO-234567',
    licenseName: 'NMLS MLO License',
    status: USER_LICENSE_STATUSES.ACTIVE,
    issuedAt: '2020-03-01',
    renewalDate: '2026-12-31',
    continuingEducationDue: '2026-12-31',
  },
  MIKE_STATE_TX: {
    id: USER_LICENSE_IDS.MIKE_STATE_TX,
    userId: USER_IDS.MIKE_CHEN,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    licenseType: LICENSE_TYPES.STATE_MLO,
    licenseNumber: 'TX-MLO-234567',
    licenseName: 'Texas MLO License',
    state: 'TX',
    status: USER_LICENSE_STATUSES.ACTIVE,
    issuedAt: '2020-03-01',
    expiresAt: '2026-12-31',
  },

  // Sarah Williams - Processor
  SARAH_NMLS: {
    id: USER_LICENSE_IDS.SARAH_NMLS,
    userId: USER_IDS.SARAH_WILLIAMS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    licenseType: LICENSE_TYPES.NMLS_INDIVIDUAL,
    licenseNumber: 'MLO-345678',
    licenseName: 'NMLS MLO License',
    status: USER_LICENSE_STATUSES.ACTIVE,
    issuedAt: '2019-08-15',
    renewalDate: '2026-12-31',
  },
  SARAH_STATE_FL: {
    id: USER_LICENSE_IDS.SARAH_STATE_FL,
    userId: USER_IDS.SARAH_WILLIAMS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    licenseType: LICENSE_TYPES.STATE_MLO,
    licenseNumber: 'FL-MLO-345678',
    licenseName: 'Florida MLO License',
    state: 'FL',
    status: USER_LICENSE_STATUSES.ACTIVE,
    issuedAt: '2019-08-15',
    expiresAt: '2026-12-31',
  },

  // David Park - Underwriter
  DAVID_NMLS: {
    id: USER_LICENSE_IDS.DAVID_NMLS,
    userId: USER_IDS.DAVID_PARK,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    licenseType: LICENSE_TYPES.NMLS_INDIVIDUAL,
    licenseNumber: 'MLO-456789',
    licenseName: 'NMLS MLO License',
    status: USER_LICENSE_STATUSES.ACTIVE,
    issuedAt: '2021-01-10',
    renewalDate: '2026-12-31',
  },

  // Emily Nguyen - Closer
  EMILY_NMLS: {
    id: USER_LICENSE_IDS.EMILY_NMLS,
    userId: USER_IDS.EMILY_NGUYEN,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    licenseType: LICENSE_TYPES.NMLS_INDIVIDUAL,
    licenseNumber: 'MLO-567890',
    licenseName: 'NMLS MLO License',
    status: USER_LICENSE_STATUSES.ACTIVE,
    issuedAt: '2022-04-01',
    renewalDate: '2026-12-31',
  },

  // 1Sharpe Capital Admin
  SHARPE_ADMIN_NMLS: {
    id: USER_LICENSE_IDS.SHARPE_ADMIN_NMLS,
    userId: USER_IDS.SHARPE_ADMIN,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    licenseType: LICENSE_TYPES.NMLS_INDIVIDUAL,
    licenseNumber: 'MLO-678901',
    licenseName: 'NMLS MLO License',
    status: USER_LICENSE_STATUSES.ACTIVE,
    issuedAt: '2019-02-01',
    renewalDate: '2026-12-31',
  },
  SHARPE_ADMIN_STATE_GA: {
    id: USER_LICENSE_IDS.SHARPE_ADMIN_STATE_GA,
    userId: USER_IDS.SHARPE_ADMIN,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    licenseType: LICENSE_TYPES.STATE_MLO,
    licenseNumber: 'GA-MLO-678901',
    licenseName: 'Georgia MLO License',
    state: 'GA',
    status: USER_LICENSE_STATUSES.ACTIVE,
    issuedAt: '2019-02-01',
    expiresAt: '2026-12-31',
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function getAllUserLicenses(workspace?: string): UserLicenseSeedData[] {
  const licenses = Object.values(USER_LICENSES);
  if (!workspace) return licenses;
  return licenses.map((l) => ({
    ...l,
    licenseName: l.licenseName ? `${l.licenseName} [${workspace}]` : undefined,
  }));
}

export function getUserLicensesByUser(userId: string): UserLicenseSeedData[] {
  return Object.values(USER_LICENSES).filter((l) => l.userId === userId);
}

export function getUserLicensesByBusiness(businessId: string): UserLicenseSeedData[] {
  return Object.values(USER_LICENSES).filter((l) => l.businessId === businessId);
}

export function getUserLicensesByState(state: string): UserLicenseSeedData[] {
  return Object.values(USER_LICENSES).filter((l) => l.state === state);
}

export function getActiveUserLicenses(): UserLicenseSeedData[] {
  return Object.values(USER_LICENSES).filter((l) => l.status === USER_LICENSE_STATUSES.ACTIVE);
}

export function getExpiringSoonLicenses(withinDays: number = 90): UserLicenseSeedData[] {
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() + withinDays);
  const cutoffStr = cutoffDate.toISOString().split('T')[0] as string;

  return Object.values(USER_LICENSES).filter(
    (l) => l.expiresAt && l.expiresAt <= cutoffStr && l.status === USER_LICENSE_STATUSES.ACTIVE,
  );
}
